import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { basePath } from '@/utils/basePath';
import { getNewsletterList, Newsletter } from '@/utils/newsletters';
import { buildNewsletterListSchema } from '@/utils/schema';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import { useRef } from 'react';

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateStr));
}

type Props = { newsletters: Omit<Newsletter, 'body'>[]; seo: SEOProps };

export const getStaticProps: GetStaticProps<Props> = async () => {
  const newsletters = getNewsletterList();
  const seo: SEOProps = {
    title: 'Newsletters | zLawyer',
    description:
      "Retrouvez toutes les newsletters zLawyer : actualités produit, guides pratiques et mise en conformité pour votre cabinet d'avocats.",
    url: `https://www.zlawyer.fr${basePath}/newsletters/`,
    schema: buildNewsletterListSchema(
      newsletters.map((n, i) => ({ title: n.title, slug: n.slug, position: i + 1 })),
    ),
  };
  return { props: { newsletters, seo } };
};

export default function Newsletters({
  newsletters,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef(null),
    title: useRef(null),
    subtitle: useRef(null),
  };
  const itemsRef = useRef<(HTMLElement | null)[]>([]);

  const setItemRef = (el: HTMLElement | null, index: number) => {
    if (el) itemsRef.current[index] = el;
  };

  useGSAP(
    () => {
      if (!refs.section.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const splitSubtitle = new SplitText(refs.subtitle.current, { mask: 'lines', type: 'lines' });
      const itemEls = itemsRef.current.filter(Boolean);

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 65%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .from(
          splitSubtitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.1',
        )
        .from(itemEls, { autoAlpha: 0, y: 12, stagger: 0.1, duration: MOTION.durationSm }, '<+0.2');

      return () => {
        splitTitle.revert();
        splitSubtitle.revert();
      };
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section flex flex-col items-center gap-12"
      id="newsletters"
    >
      <div className="flex w-full flex-col gap-3">
        <Tag ref={refs.tag}>Notre actualité</Tag>

        <h1 ref={refs.title} className="w-fit">
          Nos newsletters
        </h1>

        <p ref={refs.subtitle} className="max-w-2xl text-left">
          Guides pratiques, actualités produit et mise en conformité : retrouvez toutes nos
          communications envoyées à nos utilisateurs.
        </p>
      </div>

      <div className="w-full max-w-6xl">
        {newsletters.map((newsletter, index) => (
          <Link key={newsletter.slug} href={`/newsletters/${newsletter.slug}`}>
            <article
              ref={(el) => setItemRef(el, index)}
              className="group border-dark-blue/6 hover:bg-dark-blue/2 relative border-b px-4 py-6 transition-colors duration-300 last:border-b-0"
            >
              <div className="bg-orange pointer-events-none absolute top-0 bottom-0 left-0 w-0.5 origin-top scale-y-0 transition-transform duration-300 group-hover:scale-y-100" />

              <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[180px_1fr_auto] sm:gap-8">
                <time className="text-dark-blue/35 font-roboto-medium pt-0.5 text-sm tabular-nums">
                  {formatDate(newsletter.date)}
                </time>

                <div className="flex flex-col gap-1.5">
                  <h2 className="text-dark-blue/70 font-roboto-medium group-hover:text-dark-blue text-lg leading-snug! transition-colors duration-200">
                    {newsletter.title}
                  </h2>
                  <p className="text-dark-blue/50 text-sm leading-relaxed">
                    {newsletter.description}
                  </p>
                </div>

                <div className="hidden items-center self-center sm:flex">
                  <span className="text-orange -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
}

